import tempMee from '../../apis/tempMee';
import { SalesMetrosService, UNASSIGNED_SALES_METRO_ID } from '../../domain/SalesMetros';
import { InternalUserRoles, UsersService } from '../../domain/Users';
import {
  DsoManagersSource,
  RamUsersSource,
  UnassignedMetroSource,
} from '../../enums/ManagerListSource';
import {
  GET_ADMIN_RAM_FAILURE,
  GET_ADMIN_RAM_REQUEST,
  GET_ADMIN_RAM_SUCCESS,
  GET_DSO_MANAGERS_FAILURE,
  GET_DSO_MANAGERS_REQUEST,
  GET_DSO_MANAGERS_SUCCESS,
  GET_MANAGERS_LIST_SUCCESS,
  GET_RAM_USERS_FAILURE,
  GET_RAM_USERS_REQUEST,
  GET_RAM_USERS_SUCCESS,
} from '../actionTypes';
import { resolveManagerListSources } from './managerListSources';

/**
 * Loads the monolith's `admin/rams-dsomanagers` lists.
 *
 * `dsoRamManagers` always comes from here. `ramUsers` and `dsoManagers` come
 * from here only while their recorded source is the monolith, forwarded onto
 * the same actions their newer sources dispatch, so each slice keeps one
 * writer. `?? []` keeps a slice an array if the monolith stops sending a field.
 */
export const getAdminRamUser = () => async (dispatch) => {
  const sources = await dispatch(resolveManagerListSources());

  dispatch({ type: GET_ADMIN_RAM_REQUEST });
  try {
    const response = await tempMee.get('admin/rams-dsomanagers');

    dispatch({ type: GET_MANAGERS_LIST_SUCCESS, payload: response.data });
    if (sources.dsoManagers === DsoManagersSource.Monolith) {
      dispatch({
        type: GET_DSO_MANAGERS_SUCCESS,
        payload: response.data?.dsoManagers ?? [],
      });
    }
    if (sources.ramUsers === RamUsersSource.Monolith) {
      dispatch({
        type: GET_RAM_USERS_SUCCESS,
        payload: response.data?.ramUsers ?? [],
      });
    }
    dispatch({ type: GET_ADMIN_RAM_SUCCESS });
  } catch (err) {
    dispatch({ type: GET_ADMIN_RAM_FAILURE, payload: err });
  }
};

/**
 * Loads the DSO manager list from the auth-service role query.
 *
 * The snake_case flattening is a store-shape concern: every manager list in
 * `state.users` holds `first_name`/`last_name`.
 */
export const getDsoManagers = () => async (dispatch) => {
  dispatch({ type: GET_DSO_MANAGERS_REQUEST });
  try {
    const { data } = await UsersService.getInternalUsersByRole(InternalUserRoles.DsoManager);

    dispatch({
      type: GET_DSO_MANAGERS_SUCCESS,
      payload: data.map(({ id, firstName, lastName }) => ({
        id,
        first_name: firstName,
        last_name: lastName,
      })),
    });
  } catch (err) {
    dispatch({ type: GET_DSO_MANAGERS_FAILURE, payload: err });
  }
};

/**
 * Loads the RAM list from organization-service's sales metros.
 *
 * A metro has one name, so it lands in `first_name` with a blank `last_name`,
 * the store shape `getDsoManagers` also flattens to.
 *
 * The reserved Unassigned row stays only once the monolith resolves its zero
 * id (`unassignedMetro` recorded as the metro). Until then RamFilter offers its
 * own `'unassigned'` sentinel, and keeping the row would show a second
 * Unassigned that matches nothing.
 *
 * No fallback to the monolith on failure: an outage leaves the list empty,
 * which is visible, rather than silently serving the retired list.
 */
export const getSalesMetros = () => async (dispatch) => {
  const { unassignedMetro } = await dispatch(resolveManagerListSources());

  dispatch({ type: GET_RAM_USERS_REQUEST });
  try {
    const metros = await SalesMetrosService.getSalesMetros();
    const listed =
      unassignedMetro === UnassignedMetroSource.SalesMetro
        ? metros
        : metros.filter(({ id }) => id !== UNASSIGNED_SALES_METRO_ID);

    dispatch({
      type: GET_RAM_USERS_SUCCESS,
      payload: listed.map(({ id, name }) => ({ id, first_name: name, last_name: '' })),
    });
  } catch (err) {
    dispatch({ type: GET_RAM_USERS_FAILURE, payload: err });
  }
};

/**
 * Loads the manager lists at app start, each from its recorded source.
 *
 * The monolith request always runs, because `dsoRamManagers` has no other
 * source. The newer sources are fetched only when they own their list.
 */
export const loadManagerLists = () => async (dispatch) => {
  const sources = await dispatch(resolveManagerListSources());

  dispatch(getAdminRamUser());
  if (sources.ramUsers === RamUsersSource.SalesMetros) {
    dispatch(getSalesMetros());
  }
  if (sources.dsoManagers === DsoManagersSource.AuthService) {
    dispatch(getDsoManagers());
  }
};
